import { createTheme } from '@mui/material/styles';
import '../styles/custom.css';

/**
 * Creates the MUI theme for the given color mode.
 * Called reactively by AppThemeProvider whenever the user toggles dark/light.
 * CSS variables in custom.css handle non-MUI element theming (sidebar, header, cards).
 */
export function createAppTheme(mode) {
    const dark = mode === 'dark';

    return createTheme({
        palette: {
            mode,
            primary: {
                main: dark ? '#90caf9' : '#1976d2',
            },
            secondary: {
                main: dark ? '#f48fb1' : '#dc004e',
            },
            background: {
                default: dark ? '#121212' : '#f0f0f0',
                paper:   dark ? '#1e1e1e' : '#ffffff',
            },
            text: {
                primary:   dark ? '#e0e0e0' : '#333333',
                secondary: dark ? '#b0b0b0' : '#666666',
            },
            action: {
                edit:   dark ? 'lightgreen' : 'green',
                delete: dark ? 'lightcoral' : 'red',
            },
        },
        typography: {
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        transition: 'background-color 0.25s ease, color 0.25s ease',
                    },
                },
            },

            // ── Icon visibility ──────────────────────────────────────────
            // Ensures SVG icons always pick up the correct color from their
            // parent via currentColor, so they're visible in both modes.
            MuiSvgIcon: {
                styleOverrides: {
                    root: {
                        fill: 'currentColor',
                    },
                },
            },

            // Default IconButton color: light in dark mode, dark in light mode.
            // Inline style={{ color: '...' }} on the icon child still wins.
            MuiIconButton: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        color: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.75)'
                            : 'rgba(0, 0, 0, 0.54)',
                    }),
                },
            },

            // For Button (text/outlined variants) the icon should follow the
            // button's own text color, which MUI already sets per mode.
            // Contained buttons: MUI auto-contrasts icon against bg color.
            // ─────────────────────────────────────────────────────────────

            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                    },
                },
            },
            MuiDataGrid: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        border: `1px solid ${dark ? '#555' : '#ddd'}`,
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.paper,
                    }),
                    columnHeaders: ({ theme }) => ({
                        backgroundColor: dark ? '#333' : '#eee',
                        color: theme.palette.text.primary,
                    }),
                    cell: {
                        borderBottom: `1px solid ${dark ? '#444' : '#e0e0e0'}`,
                    },
                    row: {
                        '&:hover': {
                            backgroundColor: dark ? '#4b4b4b' : '#f5f5f5',
                        },
                        '&.Mui-selected': {
                            backgroundColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                        },
                    },
                    footerContainer: ({ theme }) => ({
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.paper,
                        borderTop: `1px solid ${dark ? '#555' : '#ddd'}`,
                    }),
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    notchedOutline: {
                        borderColor: dark ? '#555' : '#ccc',
                    },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    icon: ({ theme }) => ({
                        color: theme.palette.text.primary,
                    }),
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        '&.Mui-selected': {
                            backgroundColor: dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                        },
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        borderBottomColor: dark ? '#444' : '#e0e0e0',
                        color: theme.palette.text.primary,
                    }),
                    head: {
                        backgroundColor: dark ? '#333' : '#f5f5f5',
                        fontWeight: 600,
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: ({ theme }) => ({
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                    }),
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        backgroundColor: dark ? '#555' : '#333',
                        color: '#fff',
                        fontSize: '0.75rem',
                    },
                },
            },
        },
    });
}
